import { useMutation, useQuery, useQueryClient } from 'react-query';
import { customerApi } from './api';
import type {
  CustomerFilters,
  CustomerUpdateValues,
  CustomerCreateValues,
} from './types';

/* ----------------------------------------------------
   ĐỊNH NGHĨA QUERY KEY CHUẨN – tránh cache conflict
----------------------------------------------------- */

const CUSTOMER_LIST_KEY = 'customers:list';
const CUSTOMER_SUMMARY_KEY = 'customers:summary';
const CUSTOMER_DETAIL_KEY = 'customers:detail';
const CUSTOMER_TYPES_KEY = 'customers:types';

/* ----------------------------------------------------
   1) LẤY DANH SÁCH KHÁCH HÀNG (CÓ subscriptions)
----------------------------------------------------- */

export const useCustomerList = (filters: CustomerFilters = {}) => {
  return useQuery(
    [CUSTOMER_LIST_KEY, filters],
    () => customerApi.list(filters),
    {
      staleTime: 5 * 60 * 1000, // 5 phút
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
};

/* ----------------------------------------------------
   2) TÓM TẮT TRẠNG THÁI KHÁCH HÀNG
----------------------------------------------------- */

export const useCustomerSummary = (filters: CustomerFilters = {}) => {
  return useQuery(
    [CUSTOMER_SUMMARY_KEY, filters],
    () => customerApi.summary(filters),
    {
      keepPreviousData: true,
    }
  );
};

/* ----------------------------------------------------
   3) LẤY CHI TIẾT MỘT KHÁCH HÀNG
----------------------------------------------------- */

export const useCustomer = (id: string) => {
  return useQuery([CUSTOMER_DETAIL_KEY, id], () => customerApi.get(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/* ----------------------------------------------------
   4) TẠO KHÁCH HÀNG MỚI
----------------------------------------------------- */

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: CustomerCreateValues) => customerApi.create(payload),
    {
      onSuccess: () => {
        // clear cache của danh sách / summary
        queryClient.invalidateQueries({ queryKey: [CUSTOMER_LIST_KEY] });
        queryClient.invalidateQueries({ queryKey: [CUSTOMER_SUMMARY_KEY] });
      },
    }
  );
};

/* ----------------------------------------------------
   5) CẬP NHẬT KHÁCH HÀNG
----------------------------------------------------- */

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, payload }: { id: string; payload: CustomerUpdateValues }) =>
      customerApi.update(id, payload),
    {
      onSuccess: (updated) => {
        // update cache khách hàng chi tiết
        queryClient.setQueryData([CUSTOMER_DETAIL_KEY, updated.id], updated);

        // refresh danh sách
        queryClient.invalidateQueries({ queryKey: [CUSTOMER_LIST_KEY] });
      },
    }
  );
};

/* ----------------------------------------------------
   6) KÍCH HOẠT / VÔ HIỆU HÓA KHÁCH HÀNG
----------------------------------------------------- */

export const useToggleCustomerStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      customerApi.toggleStatus(id, status),
    {
      onSuccess: (updated) => {
        // update detail
        queryClient.setQueryData([CUSTOMER_DETAIL_KEY, updated.id], updated);

        // refresh danh sách, summary
        queryClient.invalidateQueries({ queryKey: [CUSTOMER_LIST_KEY] });
        queryClient.invalidateQueries({ queryKey: [CUSTOMER_SUMMARY_KEY] });
      },
    }
  );
};

/* ----------------------------------------------------
   7) XÓA (SOFT DELETE) KHÁCH HÀNG
----------------------------------------------------- */

export const useRemoveCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation((id: string) => customerApi.remove(id), {
    onSuccess: (_, removedId) => {
      // remove detail from cache
      queryClient.removeQueries([CUSTOMER_DETAIL_KEY, removedId]);

      // refresh danh sách
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_SUMMARY_KEY] });
    },
  });
};

/* ----------------------------------------------------
   8) RESET PASSWORD ADMIN CÔNG TY
----------------------------------------------------- */

export const useResetCustomerAdminPassword = () => {
  return useMutation((customerId: string) =>
    customerApi.resetPassword(customerId)
  );
};

/* ----------------------------------------------------
   9) LẤY DANH SÁCH LOẠI KHÁCH HÀNG
----------------------------------------------------- */

export const useCustomerTypes = () => {
  return useQuery([CUSTOMER_TYPES_KEY], () => customerApi.getCustomerTypes(), {
    staleTime: 10 * 60 * 1000, // 10 phút
    cacheTime: 30 * 60 * 1000,
  });
};
